import { FC, KeyboardEvent, useEffect, useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel, Stack, TextField, Typography } from '@mui/material';
import useVideoStore from '@/stores/videoStore';
import { formatTime } from '@/helpers/formatTime';
import { COMMENT_MAX_LENGTH } from '../config';
import { useCommentActions } from '../hooks/useCommentActions';

interface CommentComposerProps {
  noteId: string;
  shareToken?: string | null;
  /** When set, the new comment is a reply to this top-level comment. */
  parentId?: string | null;
  placeholder?: string;
  autoFocus?: boolean;
  /** Show the "Timestamp" toggle (top-level composer on a video note only). */
  showTimestampToggle?: boolean;
  submitLabel?: string;
  onSubmitted?: () => void;
  onCancel?: () => void;
}

/**
 * Shared composer for top-level comments and replies. For top-level comments on
 * a video note it captures the current playback position as the timestamp.
 */
const CommentComposer: FC<CommentComposerProps> = ({
  noteId,
  shareToken,
  parentId = null,
  placeholder = 'Add a comment…',
  autoFocus = false,
  showTimestampToggle = false,
  submitLabel = 'Comment',
  onSubmitted,
  onCancel,
}) => {
  const { videoRef } = useVideoStore();
  const { create, isSubmitting } = useCommentActions({ noteId, shareToken });
  const [body, setBody] = useState('');
  const [attachTime, setAttachTime] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);

  const canAttachTime = showTimestampToggle && Boolean(videoRef?.current);

  useEffect(() => {
    const video = videoRef?.current;
    if (!canAttachTime || !attachTime || !video) return;

    const handleTimeUpdate = () => setCurrentTime(video.currentTime);
    video.addEventListener('timeupdate', handleTimeUpdate);
    setCurrentTime(video.currentTime);
    return () => video.removeEventListener('timeupdate', handleTimeUpdate);
  }, [canAttachTime, attachTime, videoRef]);

  const handleSubmit = async () => {
    const text = body.trim();
    if (!text || isSubmitting) return;
    const videoTime = canAttachTime && attachTime && videoRef?.current ? videoRef.current.currentTime : null;
    const result = await create({ body: text, parentId, videoTime });
    if (result) {
      setBody('');
      onSubmitted?.();
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void handleSubmit();
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <TextField
        value={body}
        onChange={(e) => setBody(e.target.value.slice(0, COMMENT_MAX_LENGTH))}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoFocus={autoFocus}
        multiline
        minRows={1}
        maxRows={6}
        fullWidth
        size="small"
        inputProps={{ 'aria-label': parentId ? 'Write a reply' : 'Write a comment' }}
      />
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: 0.75, columnGap: 1 }}
      >
        {canAttachTime ? (
          <FormControlLabel
            control={<Checkbox checked={attachTime} onChange={(e) => setAttachTime(e.target.checked)} size="small" />}
            label={
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <Typography variant="caption">Timestamp</Typography>
                {attachTime && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      px: 0.75,
                      py: 0.125,
                      borderRadius: '4px',
                      backgroundColor: 'grey.100',
                      fontWeight: 500,
                      lineHeight: 1.6,
                      letterSpacing: '0.02em',
                    }}
                  >
                    {formatTime(currentTime)}
                  </Typography>
                )}
              </Stack>
            }
            sx={{ ml: 0.25 }}
          />
        ) : (
          <span />
        )}
        <Stack direction="row" spacing={1} sx={{ ml: 'auto' }}>
          {onCancel && (
            <Button size="small" color="inherit" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
          )}
          <Button
            size="small"
            variant="contained"
            onClick={() => void handleSubmit()}
            disabled={!body.trim() || isSubmitting}
          >
            {submitLabel}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default CommentComposer;
